// NOTE: can potentially look to remove this file and use more generalized version
// in common-frontend directory: "FullDateRangePicker"

import { Box, Button, Flex } from '@chakra-ui/react';
import * as _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useState, useEffect } from 'react';
import ReactDatePicker, { CalendarContainer } from 'react-datepicker';

import { utils as commonUtils } from '@finley-technologies/common';

import { colors } from 'packages/common-frontend/colors';

// need a generator function that generates a map of key to label + start + end dates
export enum DatePickerPeriod {
  past3Months = 'past3Months',
  past6Months = 'past6Months',
  pastYear = 'pastYear',
  past3Years = 'past3Years',
}

const periodToMonthsMap: Record<DatePickerPeriod, number> = {
  past3Months: 3,
  past6Months: 6,
  pastYear: 12,
  past3Years: 36,
};

const FinancialStatementsDatePickerPeriodButton = ({
  datePickerPeriod,
  onClick,
  currentSelectedDatePickerPeriod,
}: {
  datePickerPeriod: DatePickerPeriod;
  onClick: () => void;
  currentSelectedDatePickerPeriod: DatePickerPeriod | null;
}) => {
  const isCurrentlySelected =
    datePickerPeriod === currentSelectedDatePickerPeriod;

  return (
    <Button
      height="36px"
      mr="16px"
      padding="4px 12px"
      fontSize="12px"
      onClick={onClick}
      borderWidth="1px"
      borderColor={colors.gray[200]}
      backgroundColor={isCurrentlySelected ? colors.aurora[400] : 'white'}
      color={isCurrentlySelected ? 'white' : colors.black[70]}
      fontWeight={400}
    >
      {commonUtils.toTitleCase(datePickerPeriod)}
    </Button>
  );
};

type FinancialStatementsDatePickerCalendarContainerProps = {
  currentSelectedDatePickerPeriod: DatePickerPeriod | null;
  onSelectDefaultPeriod: (period: DatePickerPeriod | null) => void;
  children: React.ReactNode | React.ReactNode[];
};
const FinancialStatementsDatePickerCalendarContainer = ({
  currentSelectedDatePickerPeriod,
  onSelectDefaultPeriod,
  children,
}: FinancialStatementsDatePickerCalendarContainerProps) => {
  return (
    <CalendarContainer className="portfolio-datepicker-calendar-container">
      <Flex pl="8px">
        {_.map(DatePickerPeriod, (period) => (
          <FinancialStatementsDatePickerPeriodButton
            datePickerPeriod={period}
            onClick={() => onSelectDefaultPeriod(period)}
            currentSelectedDatePickerPeriod={currentSelectedDatePickerPeriod}
            key={`datepicker-period-button-${period}`}
          />
        ))}
        <Button
          height="36px"
          border="1px solid"
          borderColor={colors.aurora[400]}
          color={colors.aurora[400]}
          backgroundColor="white"
          padding="4px 12px"
          fontSize="12px"
          onClick={() => onSelectDefaultPeriod(null)}
        >
          Clear
        </Button>
      </Flex>
      <Box pt="16px" position="relative">
        {children}
      </Box>
    </CalendarContainer>
  );
};

type FinancialStatementsDatePickerProps = {
  onSelectDateRange: (newDateRange: [Date | null, Date | null]) => void;
  currentDateRange: { chartDataStartDate: Date; chartDataEndDate: Date };
  minDate?: Date;
  maxDate?: Date;
  width?: string;
};
export const FinancialStatementsDatePicker = ({
  onSelectDateRange,
  currentDateRange,
  minDate,
  maxDate,
  width = '50%',
}: FinancialStatementsDatePickerProps) => {
  const { chartDataStartDate, chartDataEndDate } = currentDateRange;
  // Also managing date range state down in this DatePicker so that selecting a start date
  // does not rerender the entire component and call the data refetch.
  // Refetch should only happen when start and end dates are both selected
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({ startDate: chartDataStartDate, endDate: chartDataEndDate });
  const [
    selectedDefaultPeriod,
    setSelectedDefaultPeriod,
  ] = useState<DatePickerPeriod | null>(null);

  // check to see if the currently selected dates are one of the default settings
  // even if user manually set the dates in the datepicker, we will be able to show that it is a default
  useEffect(() => {
    setDateRange({ startDate: chartDataStartDate, endDate: chartDataEndDate });
    const currentDateDay = DateTime.now()
      .setZone('America/New_York')
      .startOf('day');
    const chartDataEndDateDay = DateTime.fromJSDate(chartDataEndDate)
      .setZone('America/New_York')
      .startOf('day');
    const chartDataStartDateDay = DateTime.fromJSDate(chartDataStartDate)
      .setZone('America/New_York')
      .startOf('day');

    if (chartDataEndDateDay.toMillis() === currentDateDay.toMillis()) {
      const dateDiff = chartDataEndDateDay.diff(
        chartDataStartDateDay,
        'months',
      );
      const dateDiffMonths = dateDiff.toObject().months;

      switch (dateDiffMonths) {
        case 3:
          setSelectedDefaultPeriod(DatePickerPeriod.past3Months);
          break;
        case 6:
          setSelectedDefaultPeriod(DatePickerPeriod.past6Months);
          break;
        case 12:
          setSelectedDefaultPeriod(DatePickerPeriod.pastYear);
          break;
        case 36:
          setSelectedDefaultPeriod(DatePickerPeriod.past3Years);
          break;
        default:
          setSelectedDefaultPeriod(null);
      }
    }
  }, [chartDataStartDate, chartDataEndDate]);

  const onSelectDefaultPeriod = (period: DatePickerPeriod | null) => {
    if (!period) {
      setDateRange({ startDate: null, endDate: null });
      onSelectDateRange([null, null]);
      setSelectedDefaultPeriod(null);

      return;
    }
    const currentDate = new Date();

    const startDate = DateTime.fromJSDate(currentDate)
      .minus({ months: periodToMonthsMap[period] })
      .toJSDate();
    setDateRange({ startDate, endDate: currentDate });
    onSelectDateRange([startDate, currentDate]);
    setSelectedDefaultPeriod(period);
  };

  const { startDate, endDate } = dateRange;
  return (
    <Flex
      width={width || '50%'}
      className="portfolioDatePicker"
      sx={{
        alignItems: 'center',
        bg: 'transparent',
        borderWidth: '1px',
        borderRadius: '0.375rem',
        height: '40px',
      }}
    >
      <ReactDatePicker
        showIcon
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={(newDateRange) => {
          const [newStartDate, newEndDate] = newDateRange;
          setDateRange({ startDate: newStartDate, endDate: newEndDate });
          onSelectDateRange(newDateRange);
        }}
        isClearable
        dateFormat="MMM d, yyyy"
        placeholderText="Select date range"
        showPopperArrow={false}
        monthsShown={2}
        disabledKeyboardNavigation // required when showing multiple months to prevent react datepicker bug https://stackoverflow.com/questions/63284785/react-datepicker-remove-coloring-of-same-day-in-other-months
        calendarContainer={({ children }) => (
          <FinancialStatementsDatePickerCalendarContainer
            currentSelectedDatePickerPeriod={selectedDefaultPeriod}
            onSelectDefaultPeriod={onSelectDefaultPeriod}
          >
            {children}
          </FinancialStatementsDatePickerCalendarContainer>
        )}
        minDate={minDate}
        maxDate={maxDate}
      />
    </Flex>
  );
};
